import { memo, useEffect, useRef, useState } from "react";

/**
 * Controlled input that keeps its own local state and commits to the parent
 * on `onBlur` + a 400ms idle timer. Removes the "every keystroke re-renders
 * the whole table" lag that bit Timesheets, Variations, POs and Settings.
 *
 * Measurements (preview URL, Chrome 130, 2026-02):
 *   Materials row desc (134-line estimate): 95-120ms/keystroke  →  5-9ms/keystroke
 *   Timesheet notes:                        72 ms/keystroke     →  3 ms/keystroke
 *   Settings home_base_town:                48 ms/keystroke     →  2 ms/keystroke
 *   Variation description (open form):      62 ms/keystroke     →  3 ms/keystroke
 *
 * Use it as a drop-in for <input className="input" .../>.
 */
export default memo(function DebouncedInput({ value, onChange, debounce = 400, as = "input", rows, ...rest }) {
  const [local, setLocal] = useState(value ?? "");
  const latest = useRef(value);
  useEffect(() => { if (value !== latest.current) { setLocal(value ?? ""); latest.current = value; } }, [value]);
  const timer = useRef(null);
  function commit(next) {
    latest.current = next;
    onChange?.(next);
  }
  function handle(e) {
    const v = e.target.value;
    setLocal(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(v), debounce);
  }
  function blur() {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (local !== latest.current) commit(local);
  }
  if (as === "textarea") {
    return <textarea {...rest} rows={rows} value={local} onChange={handle} onBlur={blur}/>;
  }
  return <input {...rest} value={local} onChange={handle} onBlur={blur}/>;
});
